import {ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Optional, Output, Self} from "@angular/core";
import {AbstractControl, ControlValueAccessor, NgControl, ValidatorFn} from "@angular/forms";
import { CountryCode } from "../nz-intl-tel-input/country-code";
import { Country } from "../nz-intl-tel-input/nz-intl-tel-input-types";

@Component({
  selector: "mh-country-input",
  templateUrl: "./country-input.component.html",
  styleUrls: ["./country-input.component.less"],
})
export class CountryInputComponent implements OnInit, ControlValueAccessor {
  @Input() returnCountryName = false;
  @Output() countryCodeChange = new EventEmitter<string>();
  selectedValue!: string;
  listOfOption: Array<Country> = [];
  nzFilterOption = (): boolean => true;
  disabled: boolean = false;

  allCountries: Array<Country> = [];
  preferredCountries = ['de'];

  get isError(): boolean | null {
    return this.control.invalid && this.control.dirty && !this.selectedValue;
  }

  constructor(@Self() @Optional() private control: NgControl,
              private countryCodeData: CountryCode,
              private cd: ChangeDetectorRef) {
    this.control.valueAccessor = this;
    this.fetchCountryData();
    this.listOfOption = this.allCountries;
  }

  search(value: string): void {
    this.listOfOption = this.allCountries.filter(c => c.name.toLowerCase().includes(value.toLowerCase()));
  }

  onLocationSelected(value: string) {
    // Reactive-form writes can also trigger nz-select's ngModelChange.
    // Ignore that internal notification so it cannot overwrite an automatically
    // populated country with an empty value.
    if (value === this.control?.value) {
      this.selectedValue = value;
      return;
    }

    const selectedCountry = this.allCountries.find((country) => this.getOptionValue(country) === value);
    // Do not treat a value written by the parent form as a new user selection.
    // This prevents automatic ZIP/city updates from clearing the location again.
    if (selectedCountry && value !== this.control?.value) {
      this.countryCodeChange.emit(selectedCountry.iso2);
    } else if (!value && value !== this.control?.value) {
      this.countryCodeChange.emit("");
    }
    this.onChange(value);
  }

  getOptionValue(country: Country): string {
    return this.returnCountryName ? country.name : country.iso2;
  }

  get hasUnknownSelectedValue(): boolean {
    return !!this.selectedValue && !this.allCountries.some(
      (country) => this.getOptionValue(country) === this.selectedValue,
    );
  }

  ngOnInit() {
    if (this.preferredCountries.length) {
      this.preferredCountries.forEach((iso2) => {
        this.allCountries.sort(function(x,y){ return x.iso2 == iso2 ? -1 : y.iso2 == iso2 ? 1 : 0; });
      });
    }
  }

  onChange = (value: string) => {};
  onTouched = () => {};

  writeValue(value: string) {
    const normalizedValue = (value || "").toLowerCase();
    const selectedCountry = this.allCountries.find((country) =>
      country.name.toLowerCase() === normalizedValue || country.iso2.toLowerCase() === normalizedValue,
    ) || (normalizedValue === "uk"
      ? this.allCountries.find((country) => country.iso2.toLowerCase() === "gb")
      : undefined);
    this.selectedValue = selectedCountry ? this.getOptionValue(selectedCountry) : value;
    this.cd.markForCheck();
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  protected fetchCountryData(): void {
    this.countryCodeData.allCountries.forEach((c) => {
      const country: Country = {
        name: c[0].toString(),
        iso2: c[1].toString(),
        dialCode: c[2].toString(),
        priority: +c[3] || 0,
        areaCodes: (c[4] as string[]) || undefined,
        flagClass: c[1].toString().toUpperCase(),
        placeHolder: "",
      };

      this.allCountries.push(country);
    });
  }
}
